import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  mockAlerts,
  mockFacets,
  mockHistogram,
  mockSavedSearches,
  mockSchema,
} from '../sample/data-house-mock-data';
import { AlertRow, AsyncJob, DataHouseQuery, QueryResponse } from '../models/data-house.models';
import { ParsedTerm, QueryParserService } from '../services/query-parser.service';

/** Single parser instance for the mock layer. The service has no DI deps. */
const parser = new QueryParserService();

/**
 * HTTP interceptor for Data House mocks.
 *
 * Demonstrates both code paths the real backend will support:
 *   - Sync query (< 7-day window, < 25k rows scanned) returns results inline.
 *   - Async query (>= 14-day window OR explicit large-scan keyword) returns a job
 *     handle that resolves to "complete" after a short simulated wait.
 *
 * Disable by removing from the withInterceptors([...]) array in app.config.ts.
 */

/** Track in-progress async jobs across requests within a session. */
const asyncJobs = new Map<string, AsyncJob>();

function makeQueryId(): string {
  return `q_${Math.random().toString(36).slice(2, 10)}`;
}

function makeJobId(): string {
  return `job_${Math.random().toString(36).slice(2, 12)}`;
}

function shouldPromoteToAsync(query: DataHouseQuery): { promote: boolean; reason: string; estimatedRows: number } {
  const start = new Date(query.timeRange.start).getTime();
  const end = new Date(query.timeRange.end).getTime();
  const days = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
  const estimatedRows = days * 1_200_000;
  if (days >= 14) {
    return { promote: true, reason: `Scanning ${days} days of history (${estimatedRows.toLocaleString()} rows). Run in the background.`, estimatedRows };
  }
  return { promote: false, reason: '', estimatedRows };
}

/**
 * Match a row against a parsed KQL expression.
 *
 * Boolean composition is intentionally simplified: every term is ANDed together
 * (NOT inverts the individual term). This is enough for prototype demos; the
 * real backend does proper AST evaluation against ClickHouse.
 *
 * Bare-word `text` terms fall back to a JSON-substring match — a stand-in for
 * ClickHouse's full-text search (`multiSearchAny` / `hasToken`).
 */
function matchesFreeText(row: AlertRow, freeText: string): boolean {
  if (!freeText) return true;
  const parsed = parser.parse(freeText, mockSchema);
  if (!parsed.terms.length) return true;
  return parsed.terms.every((term) => {
    const ok = matchesTerm(row, term);
    return term.negated ? !ok : ok;
  });
}

function matchesTerm(row: AlertRow, term: ParsedTerm): boolean {
  if (term.kind === 'text') {
    if (!term.value) return true;
    return JSON.stringify(row).toLowerCase().includes(term.value.toLowerCase());
  }
  if (term.kind === 'exists') {
    const v = (row as unknown as Record<string, unknown>)[term.field ?? ''];
    return v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
  }
  if (!term.field) return true;
  const rowVal = (row as unknown as Record<string, unknown>)[term.field];
  if (rowVal === null || rowVal === undefined) return false;

  switch (term.kind) {
    case 'eq':
      return matchesEquality(rowVal, term.value ?? '');
    case 'wildcard':
      return matchesWildcard(rowVal, term.value ?? '');
    case 'list':
      return (term.values ?? []).some((v) => matchesEquality(rowVal, v));
    case 'cmp':
      return matchesComparison(rowVal, term.op!, term.value ?? '');
    case 'range':
      return matchesRange(rowVal, term.from ?? '', term.to ?? '');
    case 'cidr':
      return typeof rowVal === 'string' && matchesCidr(rowVal, term.value ?? '');
    default:
      return false;
  }
}

function matchesEquality(rowVal: unknown, value: string): boolean {
  if (Array.isArray(rowVal)) return rowVal.some((v) => String(v).toLowerCase() === value.toLowerCase());
  return String(rowVal).toLowerCase() === value.toLowerCase();
}

function matchesWildcard(rowVal: unknown, pattern: string): boolean {
  // Build a case-insensitive regex from a Lucene-style pattern (only * is supported).
  const escaped = pattern
    .toLowerCase()
    .split('*')
    .map((part) => part.replace(/[.+?^${}()|[\]\\]/g, '\\$&'))
    .join('.*');
  const regex = new RegExp(`^${escaped}$`, 'i');
  if (Array.isArray(rowVal)) return rowVal.some((v) => regex.test(String(v)));
  return regex.test(String(rowVal));
}

function matchesComparison(rowVal: unknown, op: '>' | '>=' | '<' | '<=', value: string): boolean {
  const a = toComparable(rowVal);
  const b = toComparable(value);
  if (a === null || b === null) return false;
  switch (op) {
    case '>': return a > b;
    case '>=': return a >= b;
    case '<': return a < b;
    case '<=': return a <= b;
  }
}

function matchesRange(rowVal: unknown, from: string, to: string): boolean {
  const v = toComparable(rowVal);
  const lo = toComparable(from);
  const hi = toComparable(to);
  if (v === null || lo === null || hi === null) return false;
  return v >= lo && v <= hi;
}

function toComparable(v: unknown): number | null {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
      const t = Date.parse(v);
      return Number.isFinite(t) ? t : null;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function matchesCidr(ip: string, cidr: string): boolean {
  const [base, bitsRaw] = cidr.split('/');
  const bits = Number(bitsRaw);
  if (!Number.isFinite(bits) || bits < 0 || bits > 32) return false;
  const ipNum = ipToInt(ip);
  const baseNum = ipToInt(base);
  if (ipNum === null || baseNum === null) return false;
  if (bits === 0) return true;
  const mask = (~0 << (32 - bits)) >>> 0;
  return (ipNum & mask) === (baseNum & mask);
}

function ipToInt(ip: string): number | null {
  const parts = ip.split('.');
  if (parts.length !== 4) return null;
  let n = 0;
  for (const p of parts) {
    const octet = Number(p);
    if (!Number.isFinite(octet) || octet < 0 || octet > 255) return null;
    n = (n * 256) + octet;
  }
  return n >>> 0;
}

function applyFilters(rows: AlertRow[], query: DataHouseQuery): AlertRow[] {
  const start = new Date(query.timeRange.start).getTime();
  const end = new Date(query.timeRange.end).getTime();
  return rows.filter((row) => {
    const ts = new Date(row.event_occurred_on).getTime();
    if (ts < start || ts > end) return false;
    for (const [field, values] of Object.entries(query.structuredFilters)) {
      if (!values.length) continue;
      const rowVal = (row as unknown as Record<string, unknown>)[field];
      if (Array.isArray(rowVal)) {
        if (!values.some((v) => rowVal.includes(v))) return false;
      } else if (!values.includes(rowVal as string | number)) {
        return false;
      }
    }
    return matchesFreeText(row, query.freeText);
  });
}

function respond<T>(body: T, ms = 200) {
  return of(new HttpResponse<T>({ status: 200, body })).pipe(delay(ms));
}

function parseQueryString(url: string): URLSearchParams {
  const queryIdx = url.indexOf('?');
  return new URLSearchParams(queryIdx >= 0 ? url.substring(queryIdx + 1) : '');
}

export const dataHouseMockInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes('/v1/data-house/')) {
    return next(req);
  }

  console.log('[Data House Mock Interceptor]', req.method, req.url);

  // Schema
  if (req.method === 'GET' && req.url.endsWith('/v1/data-house/alerts/schema/')) {
    return respond(mockSchema, 180);
  }

  // Saved searches
  if (req.method === 'GET' && req.url.endsWith('/v1/data-house/saved-searches/')) {
    return respond(mockSavedSearches, 200);
  }
  if (req.method === 'POST' && req.url.endsWith('/v1/data-house/saved-searches/')) {
    const body = req.body as { name: string; query: DataHouseQuery };
    const created = {
      id: Date.now(),
      name: body.name,
      query: body.query,
      created_at: new Date().toISOString(),
    };
    return respond(created, 250);
  }

  // Run query — decides sync vs async
  if (req.method === 'POST' && req.url.endsWith('/v1/data-house/alerts/query/')) {
    const query = req.body as DataHouseQuery;
    const { promote, reason, estimatedRows } = shouldPromoteToAsync(query);
    if (promote) {
      const jobId = makeJobId();
      const job: AsyncJob = {
        job_id: jobId,
        status: 'running',
        progress: 5,
        submitted_at: new Date().toISOString(),
        label: describeQuery(query),
      };
      asyncJobs.set(jobId, job);
      // Advance the job over time so the next poll sees progress.
      const start = Date.now();
      const tick = () => {
        const j = asyncJobs.get(jobId);
        if (!j || j.status === 'complete') return;
        const elapsed = (Date.now() - start) / 1000;
        if (elapsed >= 4) {
          const matched = applyFilters(mockAlerts, query);
          asyncJobs.set(jobId, {
            ...j,
            status: 'complete',
            progress: 100,
            query_id: makeQueryId(),
            total: matched.length,
            rows_scanned: estimatedRows,
            duration_ms: 3987,
          });
        } else {
          asyncJobs.set(jobId, { ...j, progress: Math.min(95, Math.round(elapsed * 24)) });
          setTimeout(tick, 400);
        }
      };
      setTimeout(tick, 400);
      const response: QueryResponse = { status: 'queued', job_id: jobId, reason, rows_to_scan: estimatedRows };
      return respond(response, 300);
    }
    const matched = applyFilters(mockAlerts, query);
    const response: QueryResponse = {
      status: 'complete',
      query_id: makeQueryId(),
      total: matched.length,
      rows_scanned: Math.max(matched.length, 5000),
      duration_ms: 412,
    };
    return respond(response, 500);
  }

  // Async job poll
  const jobMatch = req.url.match(/\/v1\/data-house\/alerts\/query\/([^/]+)\/$/);
  if (req.method === 'GET' && jobMatch) {
    const job = asyncJobs.get(jobMatch[1]);
    if (job) return respond(job, 150);
  }

  // Histogram
  if (req.method === 'POST' && req.url.endsWith('/v1/data-house/alerts/histogram/')) {
    const query = req.body as DataHouseQuery;
    const matched = applyFilters(mockAlerts, query);
    const buckets = new Map<string, number>();
    for (const row of matched) {
      const day = row.event_occurred_on.substring(0, 10);
      buckets.set(day, (buckets.get(day) ?? 0) + 1);
    }
    const result = Array.from(buckets.entries())
      .map(([bucket, count]) => ({ bucket: `${bucket}T00:00:00Z`, count }))
      .sort((a, b) => (a.bucket < b.bucket ? -1 : 1));
    return respond(result, 400);
  }

  // Facets
  if (req.method === 'POST' && req.url.endsWith('/v1/data-house/alerts/facets/')) {
    return respond(mockFacets, 250);
  }

  // Single event detail
  const eventMatch = req.url.match(/\/v1\/data-house\/alerts\/event\/([^/]+)\/$/);
  if (req.method === 'GET' && eventMatch) {
    const trackingId = eventMatch[1];
    const row = mockAlerts.find((a) => a.tracking_id === trackingId);
    if (row) return respond(row, 200);
  }

  // OData-paginated results
  if (req.method === 'GET' && req.url.includes('/v1/data-house/alerts/results/')) {
    const params = parseQueryString(req.url);
    const skip = Number(params.get('$skip') ?? '0');
    const top = Number(params.get('$top') ?? '25');
    const search = (params.get('$search') ?? '').replace(/^'(.*)'$/, '$1').toLowerCase();
    const severity = (params.get('severity') ?? '').toLowerCase();
    const start = params.get('time_start');
    const end = params.get('time_end');
    const freeText = params.get('free_text') ?? '';

    let filtered = mockAlerts;
    if (start && end) {
      const s = new Date(start).getTime();
      const e = new Date(end).getTime();
      filtered = filtered.filter((row) => {
        const ts = new Date(row.event_occurred_on).getTime();
        return ts >= s && ts <= e;
      });
    }
    if (severity) {
      const list = severity.split(',');
      filtered = filtered.filter((row) => list.includes(row.severity));
    }
    if (search) {
      filtered = filtered.filter((row) =>
        [row.event_name, row.source_ip, row.destination_ip, row.customer]
          .some((v) => v.toLowerCase().includes(search))
      );
    }
    if (freeText) {
      filtered = filtered.filter((row) => matchesFreeText(row, freeText));
    }
    const page = filtered.slice(skip, skip + top);
    return respond({ '@odata.count': filtered.length, value: page }, 350);
  }

  return next(req);
};

function describeQuery(query: DataHouseQuery): string {
  const parts: string[] = [];
  const days = Math.max(
    1,
    Math.round((new Date(query.timeRange.end).getTime() - new Date(query.timeRange.start).getTime()) / (1000 * 60 * 60 * 24))
  );
  parts.push(`${days}-day window`);
  for (const [k, v] of Object.entries(query.structuredFilters)) {
    if (v.length) parts.push(`${k}:${v.join('|')}`);
  }
  if (query.freeText) parts.push(query.freeText);
  return parts.join(', ');
}
